import { DateField } from '@sunwebgroup/uds-ui/fields/date-field'
import { format } from 'date-fns'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { validateAgeBetweenDates } from '@/scopes/checkout/components/helpers/Validations'
import { DateFieldRange } from '@/scopes/checkout/domain/types/checkoutPersonalDetails'
import { getErrorMessage } from '@/scopes/common/components/FormElements/getErrorMessage'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'
import { DateFormat } from '@/scopes/common/domain/types'
import { isValidDate, parseDay } from '@/scopes/common/domain/utils/dates'

const DEFAULT_DATE_FORMAT: DateFormat = 'dd/MM/yyyy'
type DateFieldValue = Date | string | undefined

export interface DateInputProps {
  name: string
  /**
   * This value should be passed from the site configuration to ensure the correct formatting and validation of the date input.
   */
  dateFormat?: DateFormat
  label?: string
  pattern?: string
  patternErrorMessage?: string
  required?: boolean
  requiredErrorMessage?: string
  formFieldClass?: string
  validateErrorMessage?: string
  dateRange?: DateFieldRange
  disabled?: boolean
  helpText?: string
}

export function DateInput({
  name,
  label,
  pattern,
  patternErrorMessage,
  required,
  requiredErrorMessage,
  validateErrorMessage,
  formFieldClass,
  dateRange,
  dateFormat = DEFAULT_DATE_FORMAT,
  disabled,
  helpText,
}: DateInputProps) {
  const intl = useIntl()
  const { formState, control } = useFormContext()
  const min = dateRange?.min
  const max = dateRange?.max
  const patternRegex = React.useMemo(
    () => (pattern ? new RegExp(pattern) : undefined),
    [pattern],
  )

  const validations = React.useMemo(
    () => ({
      ...(patternRegex ? { matchPattern: (value: DateFieldValue) => validatePattern(value, patternRegex, dateFormat) } : {}),
      ...(min || max
        ? {
            validateAge: (value: DateFieldValue) => {
              const delegateRequiredValidation = !value
              if (delegateRequiredValidation)
                return true

              const minBirthdate = min ? new Date(min) : undefined
              const maxBirthdate = max ? new Date(max) : undefined

              return validateAgeBetweenDates(value, minBirthdate, maxBirthdate, dateFormat)
            },
          }
        : {}),
    }),
    [dateFormat, max, min, patternRegex],
  )

  const {
    field: { value, onChange, onBlur, ref },
  } = useController({
    name,
    control,
    disabled,
    rules: {
      required,
      validate: validations,
    },
  })

  const { errors } = formState
  const errorMessage = getErrorMessage(name, errors, {
    patternErrorMessage,
    requiredErrorMessage,
    validateErrorMessage,
  })

  const status = useStatus({
    name,
    required,
    validations,
    shouldMarkValidStatus: (currentValue: DateFieldValue) => {
      if (typeof currentValue === 'string')
        return Boolean(currentValue.trim())

      return Boolean(currentValue)
    },
  })

  const inputValue = getDisplayValue(value, dateFormat)
  const formatHelpText = dateFormat.toUpperCase()

  return (
    <DateField
      ref={ref}
      name={name}
      label={label}
      feedbackText={errorMessage}
      status={status}
      dateFormat={dateFormat}
      formFieldClass={formFieldClass}
      disabled={disabled}
      helpText={helpText ?? formatHelpText}
      optionalLabel={required === false ? intl.formatMessage({ id: 'common_form_field_optional_label' }) : undefined}
      value={inputValue}
      onChange={(formattedValue: string) => {
        if (!formattedValue) {
          onChange('')
          return
        }

        if (!isCompleteDateValue(formattedValue, dateFormat)) {
          onChange(formattedValue)
          return
        }

        const parsedDate = parseDay(formattedValue, dateFormat)
        onChange(isValidDate(parsedDate) ? parsedDate : formattedValue)
      }}
      onBlur={onBlur}
    />
  )
}

function getDisplayValue(value: DateFieldValue, dateFormat: DateFormat) {
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return format(value, dateFormat)

  if (typeof value === 'string')
    return value

  return ''
}

function validatePattern(value: DateFieldValue, pattern: RegExp, dateFormat: DateFormat) {
  if (value instanceof Date)
    return pattern.test(format(value, dateFormat))

  if (typeof value !== 'string')
    return true

  const normalized = value.trim().replace(/\s+/g, ' ')

  if (!normalized)
    return true

  return pattern.test(normalized)
}

function isCompleteDateValue(value: string, dateFormat: DateFormat) {
  return value.length === dateFormat.length
}
